import { Button, Search, Switch } from '@appkit4/react-components';
import { LoaderWrapper } from 'components/common/helpers';
import { Floater, ProfileItem } from 'routes/analysis/helpers/analysisHelpers';
import { FC, useCallback, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { updateParams } from 'services/common';
import { ActiveFilters } from 'components/layout/filters/filters';
import Paginate from 'components/common/paginate';
import { useAtomValue } from 'jotai';
import { filterAtom } from 'jotai/store';
import { FetchProfiles } from '../queries/role';
import ProfileDetails from './profileDetails';
import ToolTip from 'components/common/tooltip';

const ProfileList: FC = () => {
  const { analysisId, profileId } = useParams()
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [filterComposite, setFilterComposite] = useState(searchParams.get("composite") === "1" ? true : false);
  const [filterUsers, setFilterUsers] = useState(searchParams.get("attached") === "1" ? true : false);

  const getAttached = useCallback(() => searchParams.get("attached") === "1" ? true : false, [searchParams]);
  const getComposite = useCallback(() => searchParams.get("composite") === "1" ? true : false, [searchParams]);
  const getCurrentPage = useCallback(() => parseInt(searchParams.get("p") || "1"), [searchParams]);
  const getOffset = useCallback(() => parseInt(searchParams.get("o") || "50"), [searchParams]);
  const getSearch = useCallback(() => searchParams.get("profileSearch") || "", [searchParams]);

  const navigate = useNavigate();

  const userFilter = useAtomValue(filterAtom);

  const { isPending, data, isFetching, error } = FetchProfiles(analysisId, userFilter, (getCurrentPage() * 50) - 50, getAttached(), getComposite(), getSearch());

  const handleClick = (id: string) => {
    if (profileId && id === profileId)
      navigate(`/analysis/${analysisId}/profiles${location.search}`);
    else
      navigate(`/analysis/${analysisId}/profiles/${id}${location.search}`);
  }
  const applySearch = () => {
    setSearchParams(updateParams("profileSearch", search, searchParams));
    setSearchParams(updateParams("attached", filterUsers ? 1 : 0, searchParams));
    setSearchParams(updateParams("composite", filterComposite ? 1 : 0, searchParams));
  }

  const resetSearch = () => {
    setSearch("");
    setFilterUsers(false);
    setFilterComposite(false);
    setSearchParams(updateParams("profileSearch", "", searchParams));
    setSearchParams(updateParams("attached", 0, searchParams));
    setSearchParams(updateParams("composite", 0, searchParams));
  }

  const activeFilters = search.length > 2 || filterUsers || filterComposite;
  return (
    <>
      <div className="flex items-center gap-4 mt-4">
        <div>
          <Search
            searchType={"secondary"}
            onChange={(value: string) => {
              setSearch(value);
            }}
            searchValue={search}
            className="list-filter"
          />
        </div>
        <div>
          <div>
            <Switch onChange={(value: boolean) => {
              setFilterUsers(value);
            }} checked={filterUsers} showIndicator>Profiles with users only</Switch>
          </div>
        </div>
        <div>
          <div>
            <Switch onChange={(value: boolean) => {
              setFilterComposite(value);
            }} checked={filterComposite} showIndicator>Composite profiles</Switch>
          </div>
        </div>
        <div>
          <Button onClick={() => applySearch()}>Apply</Button>
        </div>
        <div>
          {
            activeFilters && <Button onClick={() => resetSearch()} kind="secondary" style={{ display: "block" }}>Clear</Button>
          }
        </div>
      </div>
      {data && <ActiveFilters rows={data?.pagination.totalItems}></ActiveFilters>}
      <Floater>
        <div
          className="ap-list-header ap-business-process-header flex gap-4 p-4 pt-6 pb-6 items-center w-full"
        >
          <div className="basis-12">
          </div>
          <div className="basis-80">
            Profile name
          </div>
          <div className="basis-32">
            Profile type
          </div>
          <div className="basis-28">
            One or more matches
          </div>
          <div className={getComposite() ? "grow" : "basis-64"}>Users with the profile</div>
          {
            !getComposite() && <ToolTip content={() => (
          <>
            <p>This indicates that the profile is part of a composite profile. The list shows users assigned with the composite profile.</p>
          </>
        )} position="bottom">
          <div className="grow">Part of composite</div> <span className="Appkit4-icon icon-information-fill ap-font-16 ap-panel-information"></span>
        </ToolTip>
          }
        </div>
      </Floater>
      <LoaderWrapper loading={[isPending, isFetching]} errors={[error]} inline>
        {
          data?.profiles.map(profile => (
            <div className="list-row" key={profile.name} id={profile.name}>
              <ProfileItem key={profile.profileId} profile={profile} open={profileId === profile.profileId} onClick={() => handleClick(profile.profileId)} />
              {
                profileId === profile.profileId && (
                  <ProfileDetails profile={profile} />
                )
              }
            </div>
          ))
        }
      </LoaderWrapper>
      {data && !isPending && (<Paginate
        noPageSet
        getTotalPages={Math.ceil(data?.pagination.totalItems / getOffset())}
      />)}
    </>
  );
}

export default ProfileList;